import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { Tabs, Button, Switch, Select, List, Typography, Tooltip, Row } from "antd";
import { QuestionCircleOutlined, TrophyFilled, FieldTimeOutlined } from "@ant-design/icons";
import axios from "@common/axios";
import useUrlHash from "@hooks/useUrlHash";
import { ContentContext } from "@context/ContentContext";
import { BrandContext } from "@context/BrandContext";
import CircularLoader from "@common/CircularLoader";
import Alert from "@common/Alert";

const { Option } = Select;
const { Text } = Typography;

const ScorcherFlame = ({ level }) => {
    const isDoubleDigit = level > 9;
    const textClass = `ch-scorcher-flame-text ${isDoubleDigit ? 'ch-scorcher-flame-text-double' : ''}`;

    return (
        <div className="ch-scorcher-flame-wrapper" aria-label={`Scorcher Level ${level}`} title={`Scorcher Level ${level}`}>
            <svg viewBox="0 0 24 24" className="ch-scorcher-flame-svg" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                <defs>
                    <linearGradient id="flameOuter" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#FFB703" />
                        <stop offset="100%" stopColor="#E85D04" />
                    </linearGradient>
                    <linearGradient id="flameInner" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#FFE45E" />
                        <stop offset="100%" stopColor="#FF9E00" />
                    </linearGradient>
                </defs>
                <path d="M12 2.5C12 2.5 6.5 8 6.5 13.5C6.5 16.5376 8.96243 19 12 19C15.0376 19 17.5 16.5376 17.5 13.5C17.5 8 12 2.5 12 2.5Z" fill="url(#flameOuter)"/>
                <path d="M12 8.5C12 8.5 9 12 9 14.5C9 16.1569 10.3431 17.5 12 17.5C13.6569 17.5 15 16.1569 15 14.5C15 12 12 8.5 12 8.5Z" fill="url(#flameInner)"/>
            </svg>
            <span className={textClass} aria-hidden="true">{level}</span>
        </div>
    );
};

const renderRank = (rank, isUser) => {
    const classes = `ch-scorcher-rank ${isUser ? 'user-rank' : ''}`;
    const rankNumberStyle = { fontSize: '18px', marginLeft: '8px' };

    if (rank === 1) {
        return (
            <div className={classes}>
                <TrophyFilled style={{ color: '#FADB14', fontSize: '28px' }} aria-hidden="true" />
                <Text strong style={rankNumberStyle}>1</Text>
                <span className="ch-sr-only">Rank 1</span>
            </div>
        );
    }
    if (rank === 2) {
        return (
            <div className={classes}>
                <TrophyFilled style={{ color: '#B5B5B6', fontSize: '28px' }} aria-hidden="true" />
                <Text strong style={rankNumberStyle}>2</Text>
                <span className="ch-sr-only">Rank 2</span>
            </div>
        );
    }
    if (rank === 3) {
        return (
            <div className={classes}>
                <TrophyFilled style={{ color: '#E18751', fontSize: '28px' }} aria-hidden="true" />
                <Text strong style={rankNumberStyle}>3</Text>
                <span className="ch-sr-only">Rank 3</span>
            </div>
        );
    }

    return (
        <div className={classes}>
            <Text strong style={{ fontSize: '18px' }}>
                <span className="ch-sr-only">Rank </span>{rank || ''}
            </Text>
        </div>
    );
};

const VALID_TABS = ['world', 'school', 'class'];

const LessonScorcher = props => {
    const { callback, hashName = 'scorcher' } = props;
    const { content, setDialogMediaObject } = useContext(ContentContext);
    const brandConfig = useContext(BrandContext);
    const conceptId = content?.currentConcept?.conceptId;

    const { hashSegments, updateHash } = useUrlHash();
    const isOurHash = hashSegments[0] === hashName;
    const tabFromHash  = isOurHash && VALID_TABS.includes(hashSegments[1]) ? hashSegments[1] : null;
    const classIdFromHash = tabFromHash === 'class' && hashSegments[2] ? parseInt(hashSegments[2], 10) : null;

    const [initData, setInitData] = useState(null);
    const [leaderboardData, setLeaderboardData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isLeaderboardLoading, setIsLeaderboardLoading] = useState(true);
    const [initError, setInitError] = useState(false);
    const [leaderboardError, setLeaderboardError] = useState(false);
    const [activeTab, setActiveTab] = useState(tabFromHash ?? 'world');
    const [selectedClassId, setSelectedClassId] = useState(classIdFromHash);
    const [timerMode, setTimerMode] = useState(true);

    const loadLeaderboard = useCallback(async () => {
        setIsLeaderboardLoading(true);
        setLeaderboardError(false);
        try {
            const response = await axios.get('/web/ui/scorcher/leaderboard', {
                params: {
                    conceptId,
                    type: activeTab,
                    classId: activeTab === 'class' ? selectedClassId : undefined
                }
            });
            setLeaderboardData(response.data);
        } catch (error) {
            console.error("Failed to load leaderboard data", error);
            setLeaderboardError(true);
        } finally {
            setIsLeaderboardLoading(false);
        }
    }, [conceptId, activeTab, selectedClassId]);

    useEffect(() => {
        if (!conceptId) return;
        const fetchInitData = async () => {
            setIsLoading(true);
            setInitError(false);
            try {
                const response = await axios.get('/web/ui/scorcher/info', { params: { conceptId } });
                setInitData(response.data);
                if (response.data.classes && response.data.classes.length > 0) {
                    const availableIds = response.data.classes.map(c => c.classId);
                    setSelectedClassId(prev =>
                        prev && availableIds.includes(prev) ? prev : response.data.classes[0].classId
                    );
                }
            } catch (error) {
                console.error("Failed to load Scorcher initialization data", error);
                setInitError(true);
            } finally {
                setIsLoading(false);
            }
        };
        fetchInitData();
    }, [conceptId]);

    useEffect(() => {
        if (!conceptId || !initData || initData.hideScorcherLeaderboard || initData.isPublicSession) return;
        loadLeaderboard();
    }, [conceptId, initData, loadLeaderboard]);

    useEffect(() => {
        if (callback && typeof callback === 'function') callback();
    }, [callback]);

    const handleTabChange = useCallback((tab) => {
        setActiveTab(tab);
        if (tab === 'class' && selectedClassId) {
            updateHash(`${hashName}/${tab}/${selectedClassId}`);
        } else {
            updateHash(`${hashName}/${tab}`);
        }
    }, [hashName, selectedClassId, updateHash]);

    const handleClassChange = useCallback((classId) => {
        setSelectedClassId(classId);
        updateHash(`${hashName}/class/${classId}`);
    }, [hashName, updateHash]);

    const handleStartScorcher = () => {
        setDialogMediaObject({
            mediaType: 'SCORCHER',
            conceptId: conceptId,
            mediaName: brandConfig.scorcherName,
            timerMode: timerMode,
            callback: () => {
                if (!conceptId || !initData || initData.hideScorcherLeaderboard || initData.isPublicSession) return;
                loadLeaderboard();
            }
        });
    };

    const tabItems = useMemo(() => {
        const items = [{ key: "world", label: brandConfig.brandCode === 'OCR' || brandConfig.brandCode === 'SOUTH_AFRICA' ? "All" : "World" }];
        if (initData?.schoolId) items.push({ key: "school", label: "School" });
        if (initData?.classes?.length > 0) items.push({ key: "class", label: "Class" });
        return items;
    }, [initData, brandConfig]);

    if (isLoading) return <CircularLoader displayInElement={true} />;

    if (initError) {
        return <Alert type="error" title={`Failed to load ${brandConfig.scorcherName}. Please try again later.`} />;
    }

    if (initData?.htmlScorcherCount === 0) {
        return <Alert type="warning" title={`No ${brandConfig.scorcherName} available for this lesson.`} />;
    }

    const hideLeaderboard = initData?.hideScorcherLeaderboard || initData?.isPublicSession;

    return (
        <div className="ch-lesson-scorcher-wrapper">
            <h2 className="margin-b--l">{brandConfig.scorcherName}</h2>

            {hideLeaderboard && (
                <div className="margin-b--m">
                    {initData?.hideScorcherLeaderboard ? (
                        <Alert type="info" title="High scores are disabled for your school." />
                    ) : (
                        <Alert type="warning" title={`The ${brandConfig.scorcherName} leaderboard is only available to subscribers.`} />
                    )}
                </div>
            )}

            <div className="ch-scorcher-toolbar flexRow flexSpaceBetween flexAlignCentre margin-b--m margin-t--s">
                <div className="flexRow flexAlignCentre mediumGap">
                    {!hideLeaderboard && activeTab === 'class' && initData?.classes?.length > 0 && (
                        <div className="padding-r--s">
                            <Select aria-label="Select a class" value={selectedClassId} onChange={handleClassChange} style={{ width: 250 }}>
                                {initData.classes.map(cls => (
                                    <Option key={cls.classId} value={cls.classId}>{cls.className}</Option>
                                ))}
                            </Select>
                        </div>
                    )}
                </div>

                <div className="flexRow flexAlignCentre mediumGap">
                    <Tooltip title={`${brandConfig.scorcherName}s will be ranked by score then time taken.`}>
                        <button className="ch-accessible-clear-button ch-help-icon" aria-label="Ranking Information">
                            <QuestionCircleOutlined style={{fontSize: '18px'}} aria-hidden="true" />
                        </button>
                    </Tooltip>

                    <span className="ch-text-bold flexRow flexAlignCentre smallGap" id="untimed-mode-label" aria-hidden="true">
                        <FieldTimeOutlined style={{color: 'var(--on-surface-mid-colour)', fontSize: '20px'}} />
                        Untimed mode:
                    </span>
                    <Switch
                        size="small"
                        checked={!timerMode}
                        onChange={(checked) => setTimerMode(!checked)}
                        aria-labelledby="untimed-mode-label"
                    />

                    <Button type="primary" onClick={handleStartScorcher} className="margin-l--m">
                        Start {brandConfig.scorcherName}
                    </Button>
                </div>
            </div>

            {!hideLeaderboard && (
                <>
                    <div className="flexRow flexSpaceBetween flexAlignEnd margin-b--m">
                        <Tabs
                            className="ch-tabs-lvl2 flex1"
                            activeKey={activeTab}
                            onChange={handleTabChange}
                            items={tabItems}
                            aria-label="Leaderboard Filters"
                        />
                    </div>

                    {isLeaderboardLoading ? (
                        <CircularLoader displayInElement={true} />
                    ) : (
                        activeTab === 'class' && initData?.classes?.length === 0 ? (
                            <Alert type="warning" title="You are not in any classes." />
                        ) : (
                            <div className="ch-leaderboard-container rel" aria-live="polite" aria-busy={isLeaderboardLoading}>
                                {leaderboardError && (
                                    <Alert type="error" title="Failed to load the leaderboard. Please try again later." />
                                )}

                                {!leaderboardError && leaderboardData?.students?.length === 0 ? (
                                    <div className="padding-y--l">
                                        <Alert type="info" title="Compete now to be the first on the leaderboard!" />
                                    </div>
                                ) : (
                                    <List
                                        className="ch-scorcher-list"
                                        dataSource={leaderboardData?.students || []}
                                        renderItem={(student, index) => (
                                            <List.Item className={`ch-scorcher-list-item ${student.highlight ? 'ch-highlight-row' : ''}`}>
                                                <div className="ch-scorcher-col ch-scorcher-col-rank">
                                                    {renderRank(index + 1, student.highlight)}
                                                </div>
                                                <div className="ch-scorcher-col ch-scorcher-col-flame">
                                                    <ScorcherFlame level={student.scorcherLevel || 1} />
                                                </div>
                                                <div className="ch-scorcher-col ch-scorcher-col-details flex1">
                                                    <Text strong className="ch-text-large">{student.name}</Text>
                                                    <Text type="secondary" className="ch-text-small">{student.schoolName}</Text>
                                                </div>
                                                <div className="ch-scorcher-col ch-scorcher-col-score align-right">
                                                    <Text strong className="ch-text-large" aria-label={`Score: ${student.score} out of ${student.maxMark ?? 10}`}>{student.score}/{student.maxMark ?? 10}</Text>
                                                    <Text type="secondary" className="ch-text-small">
                                                        {student.timed ? <span aria-label={`Time: ${student.formattedTime}`}>{student.formattedTime}</span> : 'Untimed'}
                                                    </Text>
                                                </div>
                                            </List.Item>
                                        )}
                                    />
                                )}

                                {!leaderboardError && leaderboardData?.userResult && !leaderboardData?.students?.some(s => s.highlight) && (
                                    <div className="margin-t--m">
                                        <div className="ch-scorcher-list ch-scorcher-user-result">
                                            <div className="ch-scorcher-list-item ch-highlight-row">
                                                <div className="ch-scorcher-col ch-scorcher-col-rank">
                                                    {renderRank(leaderboardData.userResult.rank + 1, true)}
                                                </div>
                                                <div className="ch-scorcher-col ch-scorcher-col-flame">
                                                    <ScorcherFlame level={leaderboardData.userResult.scorcherLevel || 1} />
                                                </div>
                                                <div className="ch-scorcher-col ch-scorcher-col-details flex1">
                                                    <Text strong className="ch-text-large margin-l--s">{leaderboardData.userResult.name} <span className="ch-sr-only">This is your current score</span>(You)</Text>
                                                </div>
                                                <div className="ch-scorcher-col ch-scorcher-col-score align-right">
                                                    <Text strong className="ch-text-large" aria-label={`Score: ${leaderboardData.userResult.score} out of ${leaderboardData.userResult.maxMark ?? 10}`}>{leaderboardData.userResult.score}/{leaderboardData.userResult.maxMark ?? 10}</Text>
                                                    <Text type="secondary" className="ch-text-small">
                                                        {leaderboardData.userResult.timed ? <span aria-label={`Time: ${leaderboardData.userResult.formattedTime}`}>{leaderboardData.userResult.formattedTime}</span> : 'Untimed'}
                                                    </Text>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )
                    )}
                </>
            )}
        </div>
    );
};

export default LessonScorcher;